import { useMemo, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Combobox } from '@/components/common/Combobox'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { formatCurrencyPl } from '@/features/contract/components/numberFormat'
import { NumericInputBase } from '@/features/contract/components/field-renderer/inputs/NumericInputBase'
import { useContractElements } from '@/features/contract/elements/hooks'
import { useAddSource, useBanks, useDeleteSource, useFinancing, useUpdateSource } from './hooks'
import { ContractCashSchedule } from './components/ContractCashSchedule'
import { CREDIT_STATUS_LABELS, type CreditStatus, type FinancingSource } from './types'

const CREDIT_STATUS_OPTIONS = (Object.keys(CREDIT_STATUS_LABELS) as CreditStatus[])
  .map(s => ({ value: s, label: CREDIT_STATUS_LABELS[s] }))

/** Kolory faz kredytu: wniosek=szary, akceptacja=niebieski, uruchomiony=zielony, odrzucony=czerwony. */
const CREDIT_STATUS_TONE: Record<CreditStatus, string> = {
  APPLIED: 'bg-slate-100 text-slate-600',
  APPROVED: 'bg-sky-100 text-sky-700',
  DISBURSED: 'bg-emerald-100 text-emerald-700',
  REJECTED: 'bg-rose-100 text-rose-700',
}

function CreditStatusBadge({ status }: { status: CreditStatus | null }) {
  if (!status) return null
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${CREDIT_STATUS_TONE[status]}`}>
      {CREDIT_STATUS_LABELS[status]}
    </span>
  )
}

const labelCls = 'text-[11px] font-medium text-slate-600'
const inputCls =
  'rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm shadow-xs focus:outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/15'
const btnCls =
  'inline-flex items-center justify-center rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white shadow-xs hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed'
const linkCls = 'text-sm font-medium text-sky-700 hover:text-sky-900 disabled:opacity-50'

type BankOption = { value: string; label: string }

export function FinancingTab({ contractId }: { contractId: number }) {
  const financingQ = useFinancing(contractId)
  const banksQ = useBanks()
  const elementsQ = useContractElements(contractId)
  const addSource = useAddSource(contractId)
  const updateSource = useUpdateSource(contractId)
  const deleteSource = useDeleteSource(contractId)
  const confirm = useConfirm()

  const [addingCash, setAddingCash] = useState(false)
  const [addingCredit, setAddingCredit] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const bankOptions: BankOption[] = useMemo(
    () => (banksQ.data ?? []).map(b => ({ value: b.code, label: b.labelPl })),
    [banksQ.data],
  )
  const bankLabel = useMemo(() => {
    const m = new Map(bankOptions.map(o => [o.value, o.label]))
    return (code: string | null) => (code ? m.get(code) ?? code : '—')
  }, [bankOptions])

  const productLabel = useMemo(() => {
    const m = new Map<number, string>()
    ;(elementsQ.data ?? []).forEach(e => m.set(e.id, e.productLabel))
    return (id: number) => m.get(id) ?? `#${id}`
  }, [elementsQ.data])

  if (financingQ.isLoading) {
    return (
      <div className="flex flex-col w-full gap-4 p-4 md:p-6">
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm px-6 py-5 text-sm text-slate-500">
          Ładowanie finansowania…
        </section>
      </div>
    )
  }
  if (financingQ.isError || !financingQ.data) {
    return (
      <div className="flex flex-col w-full gap-4 p-4 md:p-6">
        <section className="rounded-xl border border-rose-200 bg-rose-50 px-6 py-5 text-sm text-rose-700">
          Nie udało się załadować finansowania.
        </section>
      </div>
    )
  }

  const data = financingQ.data
  const s = data.summary
  const cash = data.sources.find(src => src.kind === 'CASH')
  const credits = data.sources.filter(src => src.kind === 'CREDIT')
  const underfunded = s.difference > 0.005

  const priceDiff = s.totalAmount - s.elementPriceTotal
  const priceMismatch = Math.abs(priceDiff) > 0.005
  const missingPrice = (data.elementPrices ?? []).filter(p => p.price == null)

  const remove = async (src: FinancingSource) => {
    const isCash = src.kind === 'CASH'
    const ok = await confirm({
      title: isCash ? 'Usunąć gotówkę?' : 'Usunąć kredyt?',
      description: isCash
        ? 'Usunięcie gotówki skasuje harmonogram (zablokowane, jeśli są wpłaty).'
        : 'Źródło kredytu zostanie usunięte.',
      confirmLabel: 'Usuń',
      variant: 'destructive',
    })
    if (!ok) return
    deleteSource.mutate(src.id, {
      onError: e => toast.error(errMsg(e, 'Nie udało się usunąć źródła')),
    })
  }

  return (
    <div className="flex flex-col w-full gap-4 p-4 md:p-6">
      {/* Podsumowanie */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <header className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold tracking-tight text-slate-900">Finansowanie umowy</h3>
          <p className="text-xs text-slate-500 mt-0.5">Pokrycie = gotówka + kredyty uruchomione.</p>
        </header>
        <div className="px-6 py-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Cell label="Kwota umowy" value={formatCurrencyPl(s.totalAmount)} />
          <Cell label="Gotówka" value={formatCurrencyPl(s.cashAmount)} />
          <Cell label="Kredyt uruchomiony" value={formatCurrencyPl(s.creditDisbursed)} />
          <Cell label="Różnica (do pokrycia)" value={formatCurrencyPl(s.difference)} tone={underfunded ? 'rose' : 'neutral'} />
        </div>
        {/* Wpłaty gotówkowe (gdy jest harmonogram) */}
        {s.cashAmount > 0 && (
          <div className="border-t border-slate-100 px-6 py-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Cell label="Wpłacono" value={formatCurrencyPl(s.paidTotal)} ok={s.cashRemaining <= 0.005} />
            <Cell label="Pozostało do wpłaty" value={formatCurrencyPl(s.cashRemaining)} />
            {s.refund > 0.005 && <Cell label="Nadpłata do zwrotu" value={formatCurrencyPl(s.refund)} tone="rose" />}
          </div>
        )}

        {/* Kontrola: suma cen produktów vs kwota umowy */}
        <div className="border-t border-slate-100 px-6 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Cell label="Suma cen produktów" value={formatCurrencyPl(s.elementPriceTotal)} />
            <Cell label="Kwota umowy" value={formatCurrencyPl(s.totalAmount)} />
            <Cell label="Różnica (ceny vs umowa)" value={formatCurrencyPl(priceDiff)} tone={priceMismatch ? 'rose' : 'neutral'} />
          </div>
          {priceMismatch && (
            <p className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
              ⚠ Suma cen produktów ({formatCurrencyPl(s.elementPriceTotal)}) nie zgadza się z kwotą umowy ({formatCurrencyPl(s.totalAmount)}).
            </p>
          )}
          {missingPrice.length > 0 && (
            <p className="mt-2 text-xs text-amber-700">
              Brak ceny: {missingPrice.map(p => productLabel(p.contractElementId)).join(', ')}.
            </p>
          )}
        </div>
      </section>

      {/* Źródła finansowania */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold tracking-tight text-slate-900">Źródła finansowania</h3>
            <p className="text-xs text-slate-500 mt-0.5">Dodawaj kredyty (z bankiem, statusem i datami) oraz gotówkę.</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Linki dodawania tylko gdy coś jeszcze do pokrycia (pokryte = gotówka + uruchomione kredyty). */}
            {underfunded && !cash && !addingCash && (
              <button type="button" className={linkCls} onClick={() => setAddingCash(true)}>+ Dodaj gotówkę</button>
            )}
            {underfunded && !addingCredit && (
              <button type="button" className={linkCls} onClick={() => setAddingCredit(true)}>+ Dodaj kredyt</button>
            )}
          </div>
        </header>

        <div className="px-6 py-5 flex flex-col gap-3">
          {data.sources.length === 0 && !addingCash && !addingCredit && (
            <p className="text-sm text-slate-500">Brak źródeł - dodaj gotówkę lub kredyt powyżej.</p>
          )}

          {/* Gotówka */}
          {cash && (
            editingId === cash.id ? (
              <CashForm initialAmount={cash.amount} saving={updateSource.isPending}
                onCancel={() => setEditingId(null)}
                onSave={amount => updateSource.mutate({ sourceId: cash.id, input: { amount } }, {
                  onSuccess: () => setEditingId(null),
                  onError: e => toast.error(errMsg(e, 'Nie udało się zapisać gotówki')),
                })} />
            ) : (
              <Row title="Gotówka" amount={cash.amount}
                onEdit={() => setEditingId(cash.id)} onDelete={() => remove(cash)} busy={deleteSource.isPending} />
            )
          )}
          {addingCash && (
            <CashForm initialAmount={null} saving={addSource.isPending}
              onCancel={() => setAddingCash(false)}
              onSave={amount => addSource.mutate({ kind: 'CASH', amount }, {
                onSuccess: () => setAddingCash(false),
                onError: e => toast.error(errMsg(e, 'Nie udało się dodać gotówki')),
              })} />
          )}

          {/* Kredyty */}
          {credits.map(c => (
            editingId === c.id ? (
              <CreditForm key={c.id} initial={c} banks={bankOptions} saving={updateSource.isPending}
                onCancel={() => setEditingId(null)}
                onSave={input => updateSource.mutate({ sourceId: c.id, input }, {
                  onSuccess: () => setEditingId(null),
                  onError: e => toast.error(errMsg(e, 'Nie udało się zapisać kredytu')),
                })} />
            ) : (
              <Row key={c.id}
                title={`Kredyt · ${bankLabel(c.bankCode)}`}
                badge={<CreditStatusBadge status={c.creditStatus} />}
                meta={creditDatesText(c)}
                amount={c.amount}
                onEdit={() => setEditingId(c.id)} onDelete={() => remove(c)} busy={deleteSource.isPending} />
            )
          ))}
          {addingCredit && (
            <CreditForm initial={null} banks={bankOptions} saving={addSource.isPending}
              onCancel={() => setAddingCredit(false)}
              onSave={input => addSource.mutate({ kind: 'CREDIT', ...input }, {
                onSuccess: () => setAddingCredit(false),
                onError: e => toast.error(errMsg(e, 'Nie udało się dodać kredytu')),
              })} />
          )}
        </div>
      </section>

      {/* Harmonogram gotówki - tylko gdy jest gotówka */}
      {s.cashAmount > 0 && (
        <ContractCashSchedule contractId={contractId} cashTotal={s.cashAmount} elements={elementsQ.data ?? []} />
      )}
    </div>
  )
}

function creditDatesText(c: FinancingSource): string {
  const parts: string[] = []
  if (c.verifiedAt) parts.push(`wer. ${c.verifiedAt}`)
  if (c.approvedAt) parts.push(`akc. ${c.approvedAt}`)
  if (c.disbursedAt) parts.push(`wypł. ${c.disbursedAt}`)
  return parts.join(' · ')
}

function errMsg(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  return detail || fallback
}

function Row({ title, badge, meta, amount, onEdit, onDelete, busy }: {
  title: string; badge?: ReactNode; meta?: string; amount: number
  onEdit: () => void; onDelete: () => void; busy: boolean
}) {
  return (
    <div className="flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50/40 px-4 py-3">
      <div className="w-36 shrink-0 text-base font-semibold tabular-nums text-slate-900 whitespace-nowrap">
        {formatCurrencyPl(amount)}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-800 truncate">{title}</span>
          {badge}
        </div>
        {meta && <div className="text-xs text-slate-500 mt-0.5 truncate">{meta}</div>}
      </div>
      <div className="ml-auto flex items-center gap-3 shrink-0">
        <button type="button" className={linkCls} onClick={onEdit}>Edytuj</button>
        <button type="button" className="text-sm font-medium text-rose-600 hover:text-rose-800 disabled:opacity-50"
          onClick={onDelete} disabled={busy}>Usuń</button>
      </div>
    </div>
  )
}

function CashForm({ initialAmount, onSave, onCancel, saving }: {
  initialAmount: number | null; onSave: (amount: number) => void; onCancel: () => void; saving: boolean
}) {
  const [amount, setAmount] = useState<number | null>(initialAmount)
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-sky-200 bg-sky-50/40 px-4 py-3">
      <div className="flex flex-col gap-1 w-44">
        <label className={labelCls}>Kwota gotówki</label>
        <NumericInputBase value={amount} onChange={v => setAmount(v as number | null)} decimals={2} suffix="zł" />
      </div>
      <button type="button" className={btnCls} disabled={saving}
        onClick={() => { if (amount == null || amount < 0) { toast.error('Podaj poprawną kwotę'); return } onSave(amount) }}>
        Zapisz
      </button>
      <button type="button" className={linkCls} onClick={onCancel}>Anuluj</button>
    </div>
  )
}

function CreditForm({ initial, banks, onSave, onCancel, saving }: {
  initial: FinancingSource | null
  banks: BankOption[]
  onSave: (input: {
    amount: number; bankCode: string; creditStatus: CreditStatus | null
    verifiedAt: string | null; approvedAt: string | null; disbursedAt: string | null
  }) => void
  onCancel: () => void
  saving: boolean
}) {
  const [amount, setAmount] = useState<number | null>(initial?.amount ?? null)
  const [bank, setBank] = useState<string | null>(initial?.bankCode ?? null)
  const [status, setStatus] = useState<CreditStatus | null>(initial?.creditStatus ?? 'APPLIED')
  const [verifiedAt, setVerifiedAt] = useState(initial?.verifiedAt ?? '')
  const [approvedAt, setApprovedAt] = useState(initial?.approvedAt ?? '')
  const [disbursedAt, setDisbursedAt] = useState(initial?.disbursedAt ?? '')

  const submit = () => {
    if (amount == null || amount < 0) { toast.error('Podaj poprawną kwotę kredytu'); return }
    if (!bank) { toast.error('Wybierz bank'); return }
    onSave({
      amount, bankCode: bank, creditStatus: status,
      verifiedAt: verifiedAt || null, approvedAt: approvedAt || null, disbursedAt: disbursedAt || null,
    })
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-sky-200 bg-sky-50/40 px-4 py-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1 w-44">
          <label className={labelCls}>Kwota kredytu</label>
          <NumericInputBase value={amount} onChange={v => setAmount(v as number | null)} decimals={2} suffix="zł" />
        </div>
        <div className="flex flex-col gap-1 w-52">
          <span className={labelCls}>Bank</span>
          <Combobox<string> value={bank} onChange={setBank} options={banks}
            placeholder="Wybierz bank…" emptyOptionLabel="— Brak —" />
        </div>
        <div className="flex flex-col gap-1 w-44">
          <span className={labelCls}>Status</span>
          <Combobox<string> value={status} onChange={v => setStatus(v as CreditStatus | null)}
            options={CREDIT_STATUS_OPTIONS} placeholder="Status…" emptyOptionLabel="— Brak —" />
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <DateField label="Data weryfikacji" value={verifiedAt} onChange={setVerifiedAt} />
        <DateField label="Data akceptacji" value={approvedAt} onChange={setApprovedAt} />
        <DateField label="Data wypłaty" value={disbursedAt} onChange={setDisbursedAt} />
        <div className="flex items-end gap-3 ml-auto">
          <button type="button" className={btnCls} onClick={submit} disabled={saving}>Zapisz</button>
          <button type="button" className={linkCls} onClick={onCancel}>Anuluj</button>
        </div>
      </div>
    </div>
  )
}

function DateField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <label className={labelCls}>{label}</label>
      <input type="date" value={value} onChange={e => onChange(e.target.value)} className={inputCls} />
    </div>
  )
}

function Cell({ label, value, tone, ok }: { label: string; value: string; tone?: 'rose' | 'neutral'; ok?: boolean }) {
  const valueClass = tone === 'rose' ? 'text-rose-600' : 'text-slate-900'
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-500">{label}</div>
      <div className={`mt-1 text-base font-semibold tabular-nums ${valueClass}`}>
        {value}
        {ok && <span className="ml-2 text-emerald-600" title="Spłacone w całości">✓</span>}
      </div>
    </div>
  )
}
